/**
 * BioburdenServicesPage — public landing for the Sterile Mate Clinical
 * Deep-Disinfection & Bioburden Remediation subscription.
 * Routed at /services/bioburden.
 *
 * Design: matches the September 2026 Bioburden email campaign — corporate
 * white background, navy typography, red accent header, green warranty
 * callout. No dark backgrounds, no rainbow gradients.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Phone,
  ShieldCheck,
  Sparkles,
  Wrench,
  Stethoscope,
  Activity,
  Syringe,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_ASSETS = [
  { code: "OR", label: "OR Tables" },
  { code: "ICU", label: "ICU Beds" },
  { code: "PACU", label: "PACU Stretchers" },
  { code: "ED", label: "ED / Triage Beds" },
  { code: "L&D", label: "L&D Beds" },
  { code: "Imaging", label: "CT / IR / Cath" },
  { code: "SPD", label: "SPD Equipment" },
];

const EQUIPMENT = [
  {
    image: "equipment_or_table.jpg",
    icon: Wrench,
    tag: "OR Suite",
    title: "Mizuho Modular OR Tables",
    vector:
      "Blood and irrigation fluids run beneath carbon-fiber tops, pool inside modular rail brackets, and leak down locking detents into mechanical tilt pivots.",
    steps: [
      "Pad strip + hook-and-loop seam decontamination",
      "Hinge-pin & drop-detent AHP saturation",
      "Telescoping shroud + hydraulic sleeve scrub",
    ],
  },
  {
    image: "equipment_steris_or_table.jpg",
    icon: Wrench,
    tag: "OR Suite",
    title: "Steris OR Tables",
    vector:
      "Hydraulic column and base shroud on Steris general-surgery tables collect the same fluid pool as Mizuho — bioburden crusts onto emergency override levers, actuator rods, and rail thumb-screws.",
    steps: [
      "Column-shroud disengage + AHP interior scrub",
      "Hand-pendant coiled-cord decon",
      "Rail thumb-screw + latch remediation",
    ],
  },
  {
    image: "equipment_icu_bed.jpg",
    icon: Stethoscope,
    tag: "ICU",
    title: "Hillrom Progressa ICU Beds",
    vector:
      "Bodily secretions, blood, and dialysis fluids bypass the mattress seam and drip through deck perforations directly into articulating pivots and weigh-scale load cells.",
    steps: [
      "Cardiac Chair + Trendelenburg exposure",
      "Under-deck harness & cross-member decon",
      "Base-shroud brake, steer & caster remediation",
    ],
  },
  {
    image: "equipment_pacu_stretcher.jpg",
    icon: Activity,
    tag: "PACU / L&D",
    title: "Stryker PACU Stretchers",
    vector:
      "Post-op drainage pools along mattress retention lips and runs directly into the pneumatic Fowler cylinders and dropdown rail catches.",
    steps: [
      "Fowler piston, pivot & actuator AHP scrub",
      "Dropdown rail glide-track & latch decon",
      "Foot-end extender & O2 trough clean-out",
    ],
  },
  {
    image: "equipment_iv_poles.jpg",
    icon: Syringe,
    tag: "Mobile Assets",
    title: "IV Poles & Rolling Stands",
    vector:
      "Collar threads on IV poles trap dried blood you cannot see from the outside; caster hubs harvest hair and suture material.",
    steps: [
      "Full-height collar-thread & friction-sleeve scrub",
      "Axle hub & caster housing AHP saturation",
      "Inverted-stand fluid & particulate removal",
    ],
  },
];

const TIERS = [
  {
    name: "Essential",
    cadence: "Quarterly rotation",
    highlight: false,
    lines: ["OR tables", "ED triage beds", "Photo binder"],
  },
  {
    name: "Standard",
    cadence: "Monthly rotation",
    highlight: true,
    tag: "★ Most popular",
    lines: [
      "OR tables & ED beds",
      "IV poles & mobile resuscitating equipment",
      "Ventilators & stretchers",
      "Specialty procedure tables",
      "Audit binder + lab integration",
    ],
  },
  {
    name: "Enterprise",
    cadence: "Weekly on-site",
    highlight: false,
    lines: [
      "All Standard assets",
      "Embedded on-site tech",
      "Same-day response",
      "Joint Commission rep included",
    ],
  },
];

// ============================================================================
// PAGE
// ============================================================================
export default function BioburdenServicesPage() {
  const [accessState, setAccessState] = useState("checking"); // checking | locked | unlocked
  const [visitor, setVisitor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const raw = (() => {
      try { return localStorage.getItem("bioburden_visitor_id"); }
      catch { return null; }
    })();
    if (!raw) {
      setAccessState("locked");
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/api/bioburden/access/${encodeURIComponent(raw)}`);
        const json = await r.json();
        if (cancelled) return;
        if (json.ok) {
          setVisitor(json);
          setAccessState("unlocked");
        } else {
          try { localStorage.removeItem("bioburden_visitor_id"); } catch {}
          setAccessState("locked");
        }
      } catch {
        if (!cancelled) setAccessState("locked");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleGranted = ({ visitor_id, full_name, email }) => {
    try { localStorage.setItem("bioburden_visitor_id", visitor_id); } catch {}
    setVisitor({ visitor_id, full_name, email });
    setAccessState("unlocked");
  };

  if (accessState === "checking") {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="bioburden-loading">
        <div className="text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }

  if (accessState === "locked") {
    return (
      <div className="min-h-screen bg-white text-slate-900" data-testid="bioburden-gate-page">
        <SiteHeader />
        <BioburdenAccessGate onGranted={handleGranted} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="bioburden-services-page">
      <SiteHeader />

      <Hero />
      <StatBar />
      <EquipmentSection />
      <SafetySection />
      <SOPSection />
      <TiersSection />
      <WarrantyPledge />
      <BookingForm visitor={visitor} />
      <FooterCTA />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCESS GATE — full-name + business-email + phone
// ─────────────────────────────────────────────────────────────────────────────
function BioburdenAccessGate({ onGranted }) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg(null);
    const fd = new FormData(e.currentTarget);
    const payload = {
      full_name: (fd.get("full_name") || "").toString().trim(),
      email: (fd.get("email") || "").toString().trim(),
      phone: (fd.get("phone") || "").toString().trim(),
      company: (fd.get("company") || "").toString().trim() || null,
      role_title: (fd.get("role_title") || "").toString().trim() || null,
    };
    if (!payload.full_name || !payload.email || !payload.phone) {
      setErrorMsg("Full name, work email, and phone are all required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/bioburden/access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await r.json();
      if (!r.ok || !json.ok) {
        setErrorMsg(json.detail || "Something went wrong. Please try again.");
        return;
      }
      toast.success(`Welcome, ${payload.full_name.split(" ")[0]} — unlocking your view…`);
      onGranted({
        visitor_id: json.visitor_id,
        full_name: payload.full_name,
        email: payload.email,
      });
    } catch (err) {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-72px)] flex items-stretch bg-slate-950 text-white" data-testid="bioburden-gate">
      {/* Left image panel (hidden on small screens) */}
      <div className="hidden lg:block relative w-1/2">
        <img
          src={`${API}/api/static/bioburden/images/technician-hero.jpg`}
          alt="Sterile Mate technician in Enhanced Universal Precautions PPE"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/85 via-slate-900/60 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <div className="inline-flex items-center gap-2 bg-white/10 ring-1 ring-white/25 backdrop-blur-sm rounded-full px-3 py-1 mb-4 self-start">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
            <span className="text-[10px] font-black uppercase tracking-[0.22em] text-white">
              SOP-SM-OR-2026-004
            </span>
          </div>
          <h2 className="text-4xl xl:text-5xl font-extrabold leading-[1.05] text-white mb-4 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
            Break the chain of{" "}
            <span className="bg-[#b91c1c] text-white px-2 rounded">HAI &amp; SSI.</span>
            <br />From inside the equipment out.
          </h2>
          <ul className="space-y-2 text-sm text-slate-200">
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /><span>OEM warranties &amp; existing PM agreements remain unaffected &mdash; in writing.</span></li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /><span>Inaugural Q3 pricing locked through 2027.</span></li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /><span>Photo-documented, audit-ready binder every service.</span></li>
          </ul>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 py-14 md:py-20">
        <div className="w-full max-w-xl bg-white text-slate-900 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-[#b91c1c] mb-2">
            Restricted preview
          </p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight mb-3">
            Verify your work email to unlock the Bioburden briefing.
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed mb-5">
            This section is reserved for BioMed, SPD, Infection Prevention, and
            Facilities leadership. Enter your full name, work email, and phone &mdash;
            you&rsquo;ll get instant access.
          </p>

          <form onSubmit={onSubmit} className="space-y-3.5" data-testid="bioburden-gate-form">
            <div>
              <Label htmlFor="gate_full_name" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Full name <span className="text-[#b91c1c]">*</span>
              </Label>
              <Input
                id="gate_full_name"
                name="full_name"
                required
                placeholder="e.g. Jamie Rodriguez"
                data-testid="gate-input-name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="gate_email" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Work email <span className="text-[#b91c1c]">*</span>
              </Label>
              <Input
                id="gate_email"
                name="email"
                type="email"
                required
                placeholder="name@hospital.org"
                data-testid="gate-input-email"
                className="mt-1"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                No Gmail, Yahoo, AOL, Hotmail, or personal email addresses accepted.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="gate_phone" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Phone <span className="text-[#b91c1c]">*</span>
                </Label>
                <Input
                  id="gate_phone"
                  name="phone"
                  type="tel"
                  required
                  placeholder="(555) 555-0100"
                  data-testid="gate-input-phone"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="gate_company" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Facility / company
                </Label>
                <Input
                  id="gate_company"
                  name="company"
                  placeholder="e.g. Memorial Hermann TMC"
                  data-testid="gate-input-company"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="gate_role" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Title / role
              </Label>
              <Input
                id="gate_role"
                name="role_title"
                placeholder="Biomed Director, SPD Manager…"
                data-testid="gate-input-role"
                className="mt-1"
              />
            </div>

            {errorMsg && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-800 flex gap-2" data-testid="gate-error">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="gate-submit-btn"
              className="w-full bg-[#b91c1c] hover:bg-[#991b1b] text-white font-bold text-base py-6 rounded-lg"
            >
              {submitting ? "Verifying…" : "Unlock the Bioburden briefing →"}
            </Button>

            <p className="text-[11px] text-slate-500 leading-snug">
              By continuing, you agree to be contacted by Sterile Mate LLC about the
              Bioburden Deep-Disinfection subscription. No spam &mdash; one coordinator, one reply.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section
      className="relative bg-white border-b border-slate-200"
      data-testid="bioburden-hero"
    >
      {/* Announcement strip */}
      <div className="bg-[#b91c1c] text-white text-center py-2.5 px-4">
        <p className="text-[11px] md:text-xs font-black uppercase tracking-[0.22em]">
          <span className="mr-2">★</span>
          Launching July 2026 · Inaugural Program
          <span className="ml-2">★</span>
        </p>
      </div>

      {/* Full-bleed hero image band — mirrors the email layout */}
      <div className="relative w-full bg-slate-900">
        <img
          src={`${API}/api/static/bioburden/images/technician-hero.jpg`}
          alt="Sterile Mate technician in Enhanced Universal Precautions PPE performing under-the-hood bioburden remediation inside an OR sterilizer"
          className="w-full h-[280px] sm:h-[380px] md:h-[460px] lg:h-[540px] object-cover object-center"
        />
        {/* Left-side scrim: keeps the tech visible on the right, darkens the copy side on the left */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-900/60 to-transparent pointer-events-none" />
        {/* Bottom fade for footer polish */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-slate-950/70 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 px-4 md:px-10 pb-6 md:pb-10">
          <div className="max-w-[1600px] mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 ring-1 ring-white/25 backdrop-blur-sm rounded-full px-3 py-1 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.22em] text-white">
                SOP-SM-OR-2026-004 · Clinical Deep-Disinfection
              </span>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] max-w-5xl">
              Break the chain of{" "}
              <span className="bg-[#b91c1c] text-white px-2 rounded">HAI &amp; SSI.</span>
              <br />
              From inside the equipment out.
            </h1>
          </div>
        </div>
      </div>

      {/* Content band under the image */}
      <div className="max-w-[1600px] mx-auto px-4 md:px-10 py-10 md:py-14">
        <div className="flex items-center gap-2 mb-6 text-sm">
          <Link to="/" className="text-slate-500 hover:text-slate-900 transition-colors">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 text-slate-300" />
          <Link to="/solutions" className="text-slate-500 hover:text-slate-900 transition-colors">
            Solutions
          </Link>
          <ChevronRight className="w-4 h-4 text-slate-300" />
          <span className="font-semibold text-[#b91c1c]">Bioburden Deep-Disinfection</span>
        </div>

        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-10 items-start">
          <div>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl">
              EVS wipes the top. The bioburden that drives surgical-site infections
              lives <em>inside</em> your OR tables, stretchers, L&amp;D beds, cart washers,
              and autoclaves. Sterile Mate&rsquo;s subscription program disassembles,
              decontaminates with Accelerated Hydrogen Peroxide, and reassembles &mdash;
              on the equipment your team is already tired of trying to swab from the
              outside. Every OEM warranty stays intact, in writing.
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href="#book"
                data-testid="hero-cta-book"
                className="inline-flex items-center gap-2 bg-[#b91c1c] hover:bg-[#991b1b] text-white font-bold px-6 py-3 rounded-lg transition-colors"
              >
                Reserve your walk-through
                <ChevronRight className="w-4 h-4" />
              </a>
              <a
                href={`${API}/api/static/bioburden/sop-sm-or-2026-004.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-sop"
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-900 font-bold px-6 py-3 rounded-lg border border-slate-300 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download SOP-SM-OR-2026-004
              </a>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">
              Six years in the making
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              For six years we&rsquo;ve heard the same question from Biomed and Infection
              Prevention directors: <em>&ldquo;Who actually opens these up and cleans
              inside?&rdquo;</em> Our partner already services thousands of OR tables at
              hundreds of facilities nationwide. Starting July 2026, that program is
              yours &mdash; on a predictable monthly subscription.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STAT BAR
// ─────────────────────────────────────────────────────────────────────────────
function StatBar() {
  const stats = [
    { number: "1 in 31", copy: "hospital patients has an HAI on any given day (CDC)" },
    { number: "+$10,000", copy: "avg added cost per surgical-site infection (SSI)" },
    { number: "+3 days", copy: "additional length of stay per affected patient" },
  ];
  return (
    <section className="bg-red-50/50 border-y border-red-100" data-testid="bioburden-stat-bar">
      <div className="max-w-7xl mx-auto px-4 py-10 grid md:grid-cols-3 gap-6">
        {stats.map((s) => (
          <div
            key={s.number}
            className="bg-white rounded-xl p-6 ring-1 ring-red-100 shadow-sm"
            data-testid={`stat-${s.number.replace(/\W/g, "")}`}
          >
            <p className="text-3xl md:text-4xl font-extrabold text-[#7f1d1d] leading-none">
              {s.number}
            </p>
            <p className="text-sm text-slate-600 mt-3 leading-snug">{s.copy}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EQUIPMENT — Under-the-Hood Breakdown
// ─────────────────────────────────────────────────────────────────────────────
function EquipmentSection() {
  return (
    <section className="bg-white py-16 md:py-20" data-testid="bioburden-equipment">
      <div className="max-w-7xl mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b91c1c] mb-3">
            Under-the-Hood Breakdown
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-4">
            Where hidden bioburden actually lives.
          </h2>
          <p className="text-base text-slate-600 leading-relaxed">
            The numbers above are driven by what EVS <strong>can&rsquo;t reach</strong>: dried blood, fibrin, lipid
            residue, and biofilm trapped inside hydraulic accordions, articulating hinges, actuator casings, and
            caster hubs. Here is exactly what our clinical technicians open up on every visit.
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {EQUIPMENT.map((eq) => {
            const Icon = eq.icon;
            return (
              <div
                key={eq.title}
                className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-colors overflow-hidden flex flex-col"
                data-testid={`equipment-card-${eq.tag.replace(/\W/g, "").toLowerCase()}`}
              >
                {eq.image && (
                  <div className="relative w-full aspect-[16/9] bg-slate-100 overflow-hidden">
                    <img
                      src={`${API}/api/static/bioburden/images/${eq.image}`}
                      alt={eq.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-white/95 backdrop-blur ring-1 ring-slate-200 rounded-full px-3 py-1">
                      <Icon className="w-3.5 h-3.5 text-[#b91c1c]" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700">
                        {eq.tag}
                      </span>
                    </div>
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-lg font-bold text-slate-900 mb-2 leading-snug">
                    {eq.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-3">{eq.vector}</p>
                  <ul className="space-y-1.5 mt-auto">
                    {eq.steps.map((s) => (
                      <li key={s} className="flex gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SAFETY SECTION — PPE, chemistry, incident desk
// ─────────────────────────────────────────────────────────────────────────────
function SafetySection() {
  return (
    <section className="bg-slate-50 py-16 md:py-20 border-y border-slate-200" data-testid="bioburden-safety">
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-7 ring-1 ring-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-[#b91c1c]" />
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#b91c1c]">
              Enhanced Universal Precautions
            </p>
          </div>
          <h3 className="text-2xl font-extrabold text-slate-900 mb-3">
            Every visit, every technician.
          </h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            Deep breakdown creates aerosolized spray, aggressive brushing, and direct fluid accumulation in
            unventilated mechanical pockets. We enforce full Enhanced Universal Precautions on every asset.
          </p>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Gown:</strong> AAMI Level 3 fluid-resistant isolation.</span></li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Gloves:</strong> double-gloved heavy-duty nitrile, extended cuff.</span></li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Face &amp; Eye:</strong> full shield + fluid-shield mask.</span></li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Footwear &amp; hair:</strong> fluid-impervious shoe covers, bouffant.</span></li>
            <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span><strong>Chemistry:</strong> EPA-registered AHP, 3&ndash;5 min visible-wet dwell.</span></li>
          </ul>
        </div>

        <div className="bg-white rounded-xl p-7 ring-1 ring-orange-200">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-orange-700">
              Incident &amp; Exposure Protocol
            </p>
          </div>
          <h3 className="text-2xl font-extrabold text-slate-900 mb-3">
            Incident Desk on standby &mdash; 24 / 7.
          </h3>
          <ul className="space-y-2 text-sm text-slate-700 mb-5">
            <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" /><span><strong>Puncture:</strong> wash warm water &amp; soap 15&nbsp;sec &mdash; do not squeeze.</span></li>
            <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" /><span><strong>Mucous splash:</strong> flush at nearest eye-wash a full 15&nbsp;min.</span></li>
            <li className="flex gap-2"><ChevronRight className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" /><span><strong>Escalate</strong> to Sterile Mate Safety Operations within 1&nbsp;hour.</span></li>
          </ul>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
            <Phone className="w-5 h-5 text-orange-700 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-orange-800 mb-1">
                Sterile Mate Incident Desk
              </p>
              <p className="text-lg font-extrabold text-slate-900 leading-tight">
                (281) 456-3458<br />(800) 988-9832
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SOP + 3-hour co-validation agenda
// ─────────────────────────────────────────────────────────────────────────────
function SOPSection() {
  const agenda = [
    ["00:00 – 00:30", "In-Service Huddle — PPE, AHP dwell, sharps warnings."],
    ["00:30 – 01:15", "Table 1 (Mizuho) — pads, head/foot drops, shroud access."],
    ["01:15 – 02:00", "Bed 1 (Hillrom Progressa) — articulation, split-rail, under-deck."],
    ["02:00 – 02:30", "Stretcher & Poles (Stryker) — catches, pistons, collars."],
    ["02:30 – 03:00", "Blacklight / Protein-Swab Verification — written sign-off."],
  ];
  return (
    <section className="bg-white py-16 md:py-20" data-testid="bioburden-sop">
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-[1fr_1.1fr] gap-8 items-start">
        <div className="bg-slate-900 rounded-xl p-8 text-white sticky top-24">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-5 h-5 text-red-300" />
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-red-300">
              Published SOP
            </p>
          </div>
          <h3 className="text-2xl font-extrabold mb-3 leading-tight">
            SOP-SM-OR-2026-004
          </h3>
          <p className="text-sm text-slate-300 mb-5 leading-relaxed">
            The full clinical deep-disinfection protocol used on every visit &mdash;
            administrative controls, PPE, chemistry, per-asset breakdown, hazard
            controls, and incident reporting. Share it with your Infection
            Preventionist and Biomed Director in one click.
          </p>
          <a
            href={`${API}/api/static/bioburden/sop-sm-or-2026-004.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sop-download-btn"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-slate-900 font-bold px-5 py-2.5 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Download the PDF
          </a>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b91c1c] mb-3">
            Initial SOP Co-Validation Session
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-3">
            One-time onboarding. One signed sheet.
          </h2>
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            The first time we service each asset class we run a <strong>one-time onboarding
            session</strong> alongside your OR Prep team so every step of SOP-SM-OR-2026-004
            is witnessed, documented, and signed off &mdash; before we operate independently.
            Recurring subscription visits are shorter and asset-specific.
          </p>
          <div className="space-y-2">
            {agenda.map(([time, copy]) => (
              <div
                key={time}
                className="flex gap-4 bg-slate-50 rounded-lg p-4 border border-slate-200"
              >
                <div className="shrink-0 flex items-center gap-2 min-w-[130px]">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-bold text-slate-900 tabular-nums">
                    {time}
                  </span>
                </div>
                <p className="text-sm text-slate-700 leading-snug">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TIERS
// ─────────────────────────────────────────────────────────────────────────────
function TiersSection() {
  return (
    <section
      className="bg-slate-50 py-16 md:py-20 border-y border-slate-200"
      data-testid="bioburden-tiers"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="max-w-3xl mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b91c1c] mb-3">
            The Subscription Model
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-4">
            Predictable monthly fee. Capital equipment protected.
          </h2>
          <p className="text-base text-slate-600 leading-relaxed">
            Pricing is per-asset and scoped to your facility footprint.{" "}
            <strong>Inaugural Q3 2026 customers lock 2026 rates through end of 2027.</strong>
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {TIERS.map((tier) => (
            <div
              key={tier.name}
              className={`rounded-2xl p-6 flex flex-col ${
                tier.highlight
                  ? "bg-white ring-2 ring-[#1d4ed8] shadow-lg"
                  : "bg-white ring-1 ring-slate-200"
              }`}
              data-testid={`tier-card-${tier.name.toLowerCase()}`}
            >
              {tier.tag && (
                <span className="self-start bg-[#1d4ed8] text-white text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-full mb-3">
                  {tier.tag}
                </span>
              )}
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-1">
                {tier.cadence}
              </p>
              <h3 className="text-2xl font-extrabold text-slate-900 mb-4">
                {tier.name}
              </h3>
              <ul className="space-y-2 text-sm text-slate-700 flex-1">
                {tier.lines.map((l) => (
                  <li key={l} className="flex gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#book"
                data-testid={`tier-cta-${tier.name.toLowerCase()}`}
                className={`mt-5 inline-flex items-center justify-center gap-2 font-bold px-4 py-2.5 rounded-lg transition-colors ${
                  tier.highlight
                    ? "bg-[#1d4ed8] hover:bg-[#1e40af] text-white"
                    : "bg-slate-900 hover:bg-slate-800 text-white"
                }`}
              >
                Book a walk-through
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// WARRANTY PLEDGE
// ─────────────────────────────────────────────────────────────────────────────
function WarrantyPledge() {
  return (
    <section className="bg-white py-14" data-testid="bioburden-warranty">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8">
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-emerald-700" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 mb-2">
                The Warranty &amp; PM Pledge
              </p>
              <h3 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-3 leading-tight">
                All OEM warranties &amp; existing PM agreements remain unaffected &mdash; in writing.
              </h3>
              <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                Every chemistry we use, every torque value we set, and every disassembly procedure in
                SOP-SM-OR-2026-004 is OEM-published or OEM-approved. We document the work and ship the
                warranty paper back with the asset. If a manufacturer ever challenges a service event,
                we stand behind the documentation with you.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BOOKING FORM
// ─────────────────────────────────────────────────────────────────────────────
function BookingForm({ visitor }) {
  const [assets, setAssets] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const toggleAsset = (code) =>
    setAssets((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  const onSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      facility_name: (fd.get("facility_name") || "").toString().trim(),
      contact_name: (fd.get("contact_name") || "").toString().trim(),
      email: (fd.get("email") || "").toString().trim(),
      phone: (fd.get("phone") || "").toString().trim() || null,
      role_title: (fd.get("role_title") || "").toString().trim() || null,
      priority_assets: assets,
      preferred_date: (fd.get("preferred_date") || "").toString().trim() || null,
      tier_interest: (fd.get("tier_interest") || "").toString().trim() || null,
      asset_count_estimate: fd.get("asset_count_estimate")
        ? Number(fd.get("asset_count_estimate"))
        : null,
      notes: (fd.get("notes") || "").toString().trim() || null,
    };

    if (!payload.facility_name || !payload.contact_name || !payload.email) {
      toast.error("Facility, contact name, and email are required.");
      return;
    }

    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/bioburden/walkthrough-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await r.json();
      if (!r.ok || !json.ok) {
        toast.error(json.detail || "Something went wrong. Please try again.");
        return;
      }
      setConfirmation(json);
      e.currentTarget.reset();
      setAssets([]);
      toast.success("Walk-through request received.");
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <section id="book" className="bg-white py-16" data-testid="bioburden-book-success">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-700" />
            </div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-2">
              Walk-through request received.
            </h3>
            <p className="text-sm text-slate-600 mb-4">{confirmation.next_step}</p>
            <p className="text-xs font-mono bg-white rounded-lg py-2 px-4 inline-block border border-emerald-200 text-slate-700">
              Reference: <strong>{confirmation.reference}</strong>
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="book" className="bg-slate-900 py-16 md:py-20" data-testid="bioburden-booking-form">
      <div className="max-w-5xl mx-auto px-4">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 items-start text-white">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-red-300 mb-3">
              Book Your Walk-Through
            </p>
            <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4">
              A no-cost 30-minute survey.
            </h2>
            <p className="text-base text-slate-300 leading-relaxed mb-5">
              Tell us which assets to assess first (OR, ICU, PACU, ED) and a Sterile
              Mate coordinator will reach out within one business day.
            </p>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /><span>Inaugural Q3 pricing locked through 2027.</span></li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /><span>OEM warranties + current PM agreements unaffected.</span></li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /><span>Photo-documented, audit-ready binder every service.</span></li>
            </ul>
          </div>

          <form
            onSubmit={onSubmit}
            className="bg-white text-slate-900 rounded-2xl p-6 md:p-8 space-y-4"
            data-testid="bioburden-book-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="facility_name" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Facility <span className="text-[#b91c1c]">*</span>
                </Label>
                <Input
                  id="facility_name"
                  name="facility_name"
                  required
                  placeholder="e.g. Memorial Hermann TMC"
                  data-testid="input-facility"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="contact_name" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Your name <span className="text-[#b91c1c]">*</span>
                </Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  required
                  defaultValue={visitor?.full_name || ""}
                  placeholder="Full name"
                  data-testid="input-contact"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Work email <span className="text-[#b91c1c]">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  defaultValue={visitor?.email || ""}
                  placeholder="name@hospital.org"
                  data-testid="input-email"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  placeholder="Direct line"
                  data-testid="input-phone"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="role_title" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Title / role
                </Label>
                <Input
                  id="role_title"
                  name="role_title"
                  placeholder="Biomed Director, SPD Manager…"
                  data-testid="input-role"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="preferred_date" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Preferred date
                </Label>
                <Input
                  id="preferred_date"
                  name="preferred_date"
                  type="date"
                  data-testid="input-date"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Priority assets (multi-select)
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PRIORITY_ASSETS.map((a) => {
                  const active = assets.includes(a.code);
                  return (
                    <button
                      key={a.code}
                      type="button"
                      onClick={() => toggleAsset(a.code)}
                      data-testid={`asset-chip-${a.code.replace(/\W/g, "").toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                        active
                          ? "bg-[#b91c1c] border-[#b91c1c] text-white"
                          : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"
                      }`}
                    >
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="asset_count_estimate" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Approx. asset count
                </Label>
                <Input
                  id="asset_count_estimate"
                  name="asset_count_estimate"
                  type="number"
                  min="0"
                  placeholder="e.g. 24"
                  data-testid="input-asset-count"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="tier_interest" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                  Tier of interest
                </Label>
                <select
                  id="tier_interest"
                  name="tier_interest"
                  data-testid="input-tier"
                  className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  defaultValue=""
                >
                  <option value="">Not sure yet</option>
                  <option value="essential">Essential — quarterly</option>
                  <option value="standard">Standard — monthly (★)</option>
                  <option value="enterprise">Enterprise — weekly on-site</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Notes
              </Label>
              <Textarea
                id="notes"
                name="notes"
                placeholder="Specific assets, known bioburden pain points, survey timing…"
                rows={3}
                data-testid="input-notes"
                className="mt-1"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-walkthrough-btn"
              className="w-full bg-[#b91c1c] hover:bg-[#991b1b] text-white font-bold text-base py-6 rounded-lg"
            >
              {submitting ? "Sending…" : "Request my walk-through →"}
            </Button>
            <p className="text-[11px] text-slate-500 leading-snug">
              By submitting, you agree to be contacted by Sterile Mate LLC about the
              Bioburden Deep-Disinfection subscription. No spam &mdash; one coordinator, one reply.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FOOTER CTA
// ─────────────────────────────────────────────────────────────────────────────
function FooterCTA() {
  return (
    <section className="bg-white py-12 border-t border-slate-200">
      <div className="max-w-5xl mx-auto px-4 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">
          Sterile Mate LLC · Houston, TX
        </p>
        <p className="text-sm text-slate-600">
          MBE-certified · Non-GPO friendly · Nationwide dispatch. Every service is
          photo-documented and date-stamped for your audit binder.
        </p>
      </div>
    </section>
  );
}
